"use client";
import {
  useFileUpload,
  UploadedFile,
  type UploadConfig,
} from "./use-file-upload";
import { FilePreview } from "./file-preview";
import { ExistingFilePreview } from "./existing-file-preview";
import { Accept } from "react-dropzone";
export type { Accept } from "react-dropzone";
import { FaCamera } from "react-icons/fa";
import { useEffect, useState } from "react";

interface FileUploadProps {
  label: string;
  maxFiles?: number;
  maxSize?: number; // in MB
  fileTypes?: FileType[];
  /** Overrides the `fileTypes`-derived accept map when a preset is too broad (e.g. excluding webp from "img"). */
  accept?: Accept;
  /** Human-readable label shown in the dropzone; required when `accept` is set, since it can't be derived from `fileTypes`. */
  acceptDescription?: string;
  autoUpload?: boolean;
  onChange?: (files: string[] | string | File[] | File) => void;
  uploadConfig?: UploadConfig;
  existingFiles?: string | string[];
}

export function FileUpload({
  label,
  maxFiles = 1,
  maxSize = 5, // Default to 5MB
  fileTypes = ["img", "pdf"],
  accept,
  acceptDescription,
  autoUpload = true,
  onChange,
  uploadConfig,
  existingFiles: existingFilesProp,
}: FileUploadProps) {
  const acceptedTypesString =
    acceptDescription ?? generateUploadDescription(fileTypes);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [dismissedUrls, setDismissedUrls] = useState<string[]>([]);

  const existingFiles = (
    Array.isArray(existingFilesProp)
      ? existingFilesProp
      : existingFilesProp
        ? [existingFilesProp]
        : []
  ).filter(
    (url) =>
      !files.some((f) => f.uploadedUrl === url) && !dismissedUrls.includes(url),
  );

  // If maxFiles is 1, we only want to show existing files if no new files are uploaded
  const displayedExistingFiles =
    maxFiles === 1 && files.length > 0 ? [] : existingFiles;

  const handleChange = (
    newFiles: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[]),
  ) => {
    setFiles((prev) => {
      const next = typeof newFiles === "function" ? newFiles(prev) : newFiles;
      return next;
    });
  };

  useEffect(() => {
    if (autoUpload) {
      const uploadedUrls = files
        .map((f) => f.uploadedUrl)
        .filter((url): url is string => !!url);
      if (uploadedUrls.length > 0 || files.length === 0) {
        onChange?.(maxFiles > 1 ? uploadedUrls : uploadedUrls[0] || "");
      }
      return;
    }
    const rawFiles = files.map((f) => f.file);
    if (rawFiles.length > 0 || files.length === 0) {
      onChange?.(maxFiles > 1 ? rawFiles : rawFiles[0]);
    }
  }, [files, maxFiles, onChange, autoUpload]);

  const { getRootProps, getInputProps, isDragActive, removeFile } =
    useFileUpload({
      maxFiles,
      maxSize,
      fileTypes: accept ?? generateAcceptObject(fileTypes),
      autoUpload,
      onFilesChange: handleChange,
      uploadConfig,
      files,
    });

  const isUploading = files.some((f) => f.status === "uploading");

  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    };
  }, [files]);

  return (
    <div
      {...getRootProps()}
      className={`border-img-dashed flex flex-col justify-center gap-2 rounded-lg overflow-auto p-8 w-full text-center transition-colors duration-200 ease-in-out bg-white
        ${isDragActive ? "drag-active" : ""}
        ${isUploading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <input {...getInputProps()} />

      {files.length > 0 || displayedExistingFiles.length > 0 ? (
        <div className="flex gap-2 justify-center overflow-x-auto">
          {displayedExistingFiles.map((url) => (
            <ExistingFilePreview
              key={url}
              url={url}
              onRemove={(removedUrl) => {
                setDismissedUrls((prev) => [...prev, removedUrl]);
                if (maxFiles > 1) {
                  const updated = existingFiles.filter((u) => u !== removedUrl);
                  onChange?.(updated);
                } else {
                  onChange?.("");
                }
              }}
            />
          ))}
          {files.map((file) => (
            <FilePreview
              key={file.id}
              file={file}
              onRemove={(id) => removeFile(id, files)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col font-mulish items-center justify-center gap-2 text-gray-500">
          {/* <FaRegIdCard className="w-12 h-12 text-orange-400" /> */}
          <FaCamera className="w-12 h-12 text-orange-400" />
          <p className="font-semibold text-primary font-cabin">{label}</p>
        </div>
      )}
      <p className="text-sm font-light">
        {/* 👇 Use the new description string */}
        {acceptedTypesString} only (Max {maxSize}MB){", "}
        {maxFiles > 1 ? `Max ${maxFiles} files` : ""}
      </p>
      <button
        type="button"
        className="mt-2 max-w-full text-center mx-auto px-4 py-2 bg-[#FAFAFA] border border-gray-300 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-white hover:border-none disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={
          isUploading ||
          (maxFiles > 1 &&
            files.length + displayedExistingFiles.length >= maxFiles)
        }
      >
        {isUploading ? "Uploading..." : "Upload"}
      </button>
    </div>
  );
}

/**
 * Supported file types for file uploads
 * @example
 * const fileTypes: FileType[] = ['pdf', 'doc']
 */
export type FileType = "pdf" | "doc" | "img" | "audio";

/**
 * Maps file types to their MIME types and extensions
 */
export const FILE_TYPE_MAPPINGS = {
  pdf: {
    accept: "application/pdf",
    extensions: ["PDF"],
    simpleName: "PDF",
  },
  doc: {
    accept:
      "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extensions: ["DOC", "DOCX"],
    simpleName: "Document",
  },
  img: {
    accept: "image/jpeg,image/png,image/webp",
    extensions: ["JPG", "PNG", "WEBP"],
    simpleName: "Image",
  },
  audio: {
    accept: "audio/mpeg,audio/mp3,audio/wav,audio/ogg",
    extensions: ["MP3", "WAV", "OGG"],
    simpleName: "Audio",
  },
} as const;

/**
 * ✨ NEW ✨
 * Generates the `Accept` object required by react-dropzone.
 * @param fileTypes - Array of supported file types
 * @returns An object compatible with react-dropzone's `accept` prop.
 */
export const generateAcceptObject = (fileTypes: FileType[]): Accept => {
  const acceptObject: Accept = {};
  fileTypes.forEach((type) => {
    const mapping = FILE_TYPE_MAPPINGS[type];
    const mimeTypes = mapping.accept.split(",").map((m) => m.trim());
    const extensions = mapping.extensions.map((ext) => `.${ext.toLowerCase()}`);

    mimeTypes.forEach((mime) => {
      acceptObject[mime] = extensions;
    });
  });
  return acceptObject;
};

/**
 * Generates a human-readable description of accepted file types.
 * @param fileTypes - Array of supported file types
 * @returns Formatted string of accepted types.
 */
export const generateUploadDescription = (fileTypes: FileType[]): string =>
  `${fileTypes.map((type) => FILE_TYPE_MAPPINGS[type].simpleName).join(", ")}`;
